import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import api from "@/lib/api";
import { 
  Heart, User, Users, Mail, Phone, Lock, ArrowRight, 
  CheckCircle2, Stethoscope, Home
} from "lucide-react";

export default function PatientFamilyRegistration() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    role: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    relationship: "",
    patientName: "",
    diseaseCategory: ""
  });
  const [error, setError] = useState("");

  const roleOptions = [
    { 
      value: "patient", 
      label: "I am a Patient", 
      icon: User, 
      color: "bg-teal-500",
      description: "Receiving hospice or palliative care"
    },
    { 
      value: "caregiver", 
      label: "I am a Family Caregiver", 
      icon: Users, 
      color: "bg-purple-500",
      description: "Caring for a loved one"
    }
  ];

  const relationshipOptions = [
    { value: "spouse", label: "Spouse / Partner" },
    { value: "child", label: "Adult Child" },
    { value: "parent", label: "Parent" },
    { value: "sibling", label: "Sibling" },
    { value: "friend", label: "Friend" },
    { value: "other", label: "Other Relationship" }
  ];

  const diseaseCategories = [
    { value: "cancer", label: "Cancer" },
    { value: "heart_disease", label: "Heart Disease" },
    { value: "lung_disease", label: "Lung Disease (COPD, etc.)" },
    { value: "dementia", label: "Dementia / Alzheimer's" },
    { value: "kidney_disease", label: "Kidney Disease" },
    { value: "liver_disease", label: "Liver Disease" },
    { value: "als", label: "ALS / Neurological" },
    { value: "stroke", label: "Stroke" },
    { value: "frailty", label: "General Frailty / Debility" },
    { value: "other", label: "Other / Multiple Conditions" }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.role) {
      setError("Please select whether you are a patient or caregiver");
      return;
    }
    if (!formData.name || !formData.email || !formData.password) {
      setError("Please fill in all required fields");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (formData.role === "caregiver" && !formData.relationship) {
      setError("Please select your relationship to the patient");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/register-patient-family", {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        phone: formData.phone || null,
        relationship: formData.relationship || null,
        patient_name: formData.patientName || null,
        disease_category: formData.diseaseCategory || null
      });

      // Auto-login
      if (response.data.token) {
        localStorage.setItem("hospice_token", response.data.token);
        await login(formData.email, formData.password);
        toast.success("Welcome to Goodwin Cares!");
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background py-12 px-6">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-4">
            <Heart className="w-6 h-6" />
            <span className="font-serif text-xl">Goodwin Cares</span>
          </Link>
          <h1 className="font-serif text-3xl text-foreground mb-2">Join Goodwin Cares</h1>
          <p className="text-muted-foreground">
            Create your account to access personalized support and resources
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create Your Account</CardTitle>
            <CardDescription>
              Whether you're a patient or a family caregiver, we're here to support you on this journey.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role Selection */}
              <div className="space-y-3">
                <Label className="text-base font-medium">I am a... *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {roleOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = formData.role === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleChange("role", option.value)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-muted hover:border-muted-foreground/50'
                        }`}
                      >
                        <div className={`p-3 rounded-full ${option.color} text-white`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className="font-medium text-sm">{option.label}</span>
                        <span className="text-xs text-muted-foreground text-center">
                          {option.description}
                        </span>
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {formData.role && (
                <>
                  {/* Caregiver-specific fields */}
                  {formData.role === "caregiver" && (
                    <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h3 className="font-medium text-purple-800 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        About Your Role as Caregiver
                      </h3>
                      <div className="space-y-2">
                        <Label htmlFor="relationship">Your Relationship *</Label>
                        <Select value={formData.relationship} onValueChange={(v) => handleChange("relationship", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your relationship..." />
                          </SelectTrigger>
                          <SelectContent>
                            {relationshipOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="patientName">Patient's Name</Label>
                        <Input
                          id="patientName"
                          value={formData.patientName}
                          onChange={(e) => handleChange("patientName", e.target.value)}
                          placeholder="Name of person you're caring for"
                        />
                      </div>
                    </div>
                  )}

                  {/* Disease Category */}
                  <div className="space-y-2">
                    <Label htmlFor="diseaseCategory">
                      <Stethoscope className="w-4 h-4 inline mr-1" />
                      {formData.role === "patient" ? "Primary Diagnosis" : "Patient's Primary Diagnosis"}
                    </Label>
                    <Select value={formData.diseaseCategory} onValueChange={(v) => handleChange("diseaseCategory", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select condition..." />
                      </SelectTrigger>
                      <SelectContent>
                        {diseaseCategories.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      This helps us personalize your resources and support
                    </p>
                  </div>

                  <hr className="my-4" />

                  {/* Personal Info */}
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      <User className="w-4 h-4 inline mr-1" />
                      Your Full Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="you@email.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Password *
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        placeholder="Min. 8 characters"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Confirm *
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleChange("confirmPassword", e.target.value)}
                        placeholder="Confirm password"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full gap-2" 
                size="lg"
                disabled={loading || !formData.role}
              >
                {loading ? "Creating Account..." : "Create Account"}
                <ArrowRight className="w-4 h-4" />
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in here
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Additional Links */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Are you a healthcare partner?{" "}
            <Link to="/partner-register" className="text-primary hover:underline">
              Register as Partner
            </Link>
          </p>
          <p className="text-sm text-muted-foreground">
            Need help? Call <a href="tel:703-578-7108" className="text-primary">703-578-7108</a>
          </p>
        </div>
      </div>
    </div>
  );
}
